// «Частые темы» — design workspace screen (pwa-design@insights) on the
// engine themes: date badge + title + text. Hide (§2.8 rule 8) applies.

import { useInsightsData } from '../model/useInsightsData'
import type { InsightsTheme } from '../../../types/api'
import InsightsGate from './InsightsGate'
import InsightsEmpty from './InsightsEmpty'
import InsightsTabBar from './InsightsTabBar'
import HideButton from './HideButton'
import styles from './Topics.module.css'
import { BEFORE_PAYMENT_EMPTY } from './beforePaymentCopy'

const GROWS_NOTE = 'Раздел будет дополняться автоматически после ваших следующих разговоров'

function dateParts(iso: string) {
  const [year, month, day] = iso.split('-').map(Number)
  const parts = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long' }).formatToParts(
    new Date(year, month - 1, day),
  )
  return {
    day: parts.find((part) => part.type === 'day')?.value ?? '',
    month: parts.find((part) => part.type === 'month')?.value ?? '',
  }
}

function formatWhen(theme: InsightsTheme) {
  const from = theme.first_date ? dateParts(theme.first_date) : null
  if (!from) return null
  if (!theme.last_date || theme.last_date === theme.first_date) return { day: from.day, month: from.month }
  const to = dateParts(theme.last_date)
  if (from.month === to.month) return { day: `${from.day}–${to.day}`, month: from.month }
  return { day: `${from.day} ${from.month}`, month: `${to.day} ${to.month}` }
}

export default function Topics() {
  const insights = useInsightsData()
  return (
    <>
      <InsightsGate insights={insights}>
        {insights.data && (
          <div className={styles.page}>
            <h1 className={styles.title}>Частые темы</h1>
            <p className={styles.lead}>Эти темы чаще всего встречались в ваших разговорах.</p>
            {insights.data.themes.length === 0 ? (
              <InsightsEmpty
                title="Здесь пока пусто"
                text={insights.data.before_payment ? BEFORE_PAYMENT_EMPTY.themes : 'Здесь появятся темы, которые чаще всего будут встречаться в твоих разговорах. Пока диалогов ещё не было — начни первый, и мы соберём их для тебя.'}
              />
            ) : (
              <ul className={styles.list}>
                {insights.data.themes.map((theme) => {
                  const when = formatWhen(theme)
                  return (
                    <li key={theme.id} className={styles.card}>
                      {when && (
                        <time className={styles.when} dateTime={theme.first_date}>
                          <span className={styles.day}>{when.day}</span>
                          <span className={styles.month}>{when.month}</span>
                        </time>
                      )}
                      <div className={styles.body}>
                        <h2 className={styles.cardTitle}>{theme.title}</h2>
                        <p className={styles.cardText}>{theme.text}</p>
                      </div>
                      <HideButton
                        onHide={() => { void insights.hide('themes', theme.id) }}
                        busy={insights.hiding === `themes/${theme.id}`}
                      />
                    </li>
                  )
                })}
              </ul>
            )}
            {insights.data.notes.themes_grows && insights.data.themes.length > 0 && (
              <p className={styles.growsNote}>{GROWS_NOTE}</p>
            )}
          </div>
        )}
      </InsightsGate>
      <InsightsTabBar />
    </>
  )
}
